import { useId, useState, type RefObject } from 'react'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { Select } from '../../platform/primitives/Select'
import { CategoryBrowserLoader } from '../categories/picker'
import { MerchantBrowserLoader } from '../merchants/picker'
import type { BulkOutcome } from './runBulk'
import type { Txn } from './transactionWire'
import { shortDate } from './txnDisplay'
import type { BulkKind } from './useBulkActions'
import type { FilterOptions } from './useFilterOptions'
import './transactions.css'

/** Frozen at the moment the toolbar button was pressed: the selection moves under a run (retained rows, refreshed snapshots), the dialog's rows must not. */
export type BulkRequest = { kind: BulkKind; rows: Txn[] }

type Props = {
  request: BulkRequest | null
  options: FilterOptions
  run: (kind: BulkKind, rows: readonly Txn[], value: string | null) => Promise<BulkOutcome>
  progress: { done: number; total: number } | null
  onClose: () => void
  restoreFocusTo?: RefObject<HTMLElement | null>
  /**
   * The filters changed since this dialog opened (history Back/Forward under it).
   * Its rows were frozen at open, so they may now be rows the current filters
   * hide: Apply and Retry refuse, with the reason on screen. A run already in
   * flight is left to finish and report.
   */
  stale?: boolean
}

const STALE_ID = 'txn-bulk-stale'
const PENDING_ID = 'txn-bulk-pending'

/** The reasons Apply is refusing, as one space-separated id list, or none. */
const describedBy = (stale: boolean, nothingToApply: boolean) =>
  [stale && STALE_ID, nothingToApply && PENDING_ID].filter(Boolean).join(' ') || undefined
const STALE_NOTE = 'The filters changed, so this selection is no longer the one on screen. Close this and select again.'

const TITLE: Record<BulkKind, string> = {
  set_category: 'Set category',
  set_merchant: 'Set merchant',
  add_tag: 'Add tag',
  assign_owner: 'Assign owner',
}

/** The picker's "Account default" choice. Distinct from `''`, which is "not chosen yet". */
const ACCOUNT_DEFAULT = 'account-default'

type Phase =
  | { t: 'pick' }
  | { t: 'running' }
  | { t: 'result'; updated: number; failed: BulkOutcome['failed']; skipped: number }

export function BulkDialog(props: Props) {
  // Mounted only while a request exists, so every open starts at the picker.
  if (props.request === null) return null
  return <OpenDialog key={props.request.rows.map((r) => r.id).join()} {...props} request={props.request} />
}

/**
 * The announced counts: 25, 50, 75 and 100 percent, never every row. A live
 * region that speaks on each of 100 PATCHes is noise; the figure is a pure
 * function of the milestone, so the text only changes — and only speaks — when
 * a milestone is crossed.
 */
export function milestoneText(done: number, total: number): string {
  if (total === 0) return ''
  // Quarters, not percent: the count is a whole number of rows at each one.
  const quarters = Math.floor((done * 4) / total)
  if (quarters === 0) return ''
  return `Updating ${Math.round((total * quarters) / 4)} of ${total}`
}

function OpenDialog({ request, options, run, progress, onClose, restoreFocusTo, stale = false }: Props & { request: BulkRequest }) {
  const { kind, rows } = request
  const [value, setValue] = useState('')
  const [phase, setPhase] = useState<Phase>({ t: 'pick' })
  const tagList = useId()
  const pendingCount = rows.filter((r) => r.isPending).length
  const skipsPending = kind !== 'set_category'
  const targets = skipsPending ? rows.length - pendingCount : rows.length

  const valueToSend = (): string | null => (kind === 'assign_owner' && value === ACCOUNT_DEFAULT ? null : value.trim())
  const chosen = kind === 'add_tag' ? value.trim() !== '' : value !== ''

  async function go(batch: readonly Txn[], carried: { updated: number; skipped: number }) {
    if (stale) return
    setPhase({ t: 'running' })
    const out = await run(kind, batch, valueToSend())
    setPhase({
      t: 'result',
      updated: carried.updated + out.done.length,
      failed: out.failed,
      skipped: carried.skipped + out.skipped.length,
    })
  }

  const running = phase.t === 'running'
  return (
    <Overlay
      open
      // Closing mid-run would orphan the report; the run itself cannot be recalled.
      onClose={() => {
        if (!running) onClose()
      }}
      label={TITLE[kind]}
      size={kind === 'set_category' || kind === 'set_merchant' ? 'wide' : undefined}
      restoreFocusTo={restoreFocusTo}
    >
      <div className="dialog txn-bulk-dialog">
        <h2 className="dialog__title">{TITLE[kind]}</h2>
        {/* One live region, mounted from the first render: text put into a
            region that already exists is announced, a region inserted holding
            its text is not. It speaks at progress milestones and once at the end. */}
        <p role="status" className="sr-only">
          {phase.t === 'running' ? (progress ? milestoneText(progress.done, progress.total) : '') : null}
          {phase.t === 'result' ? summary(phase) : null}
        </p>

        {phase.t === 'pick' && (
          <>
            <p className="dialog__body">{`${rows.length} selected transaction${rows.length === 1 ? '' : 's'}.`}</p>
            {/* autoFocus, as CategoryPickerDialog (R5): on a cold cache the Overlay's first focus runs before the browser exists. */}
            {kind === 'set_category' && <CategoryBrowserLoader value={value === '' ? null : value} onPick={setValue} autoFocus />}
            {/* The merchant itself is not kept: its pressed row is the visible choice, and the run needs only the id. */}
            {kind === 'set_merchant' && <MerchantBrowserLoader value={value === '' ? null : value} onPick={(m) => setValue(m.id)} autoFocus />}
            {kind === 'assign_owner' && (
              <Select label="Owner" value={value} onChange={(e) => setValue(e.target.value)}>
                <option value="">Choose an owner</option>
                <option value={ACCOUNT_DEFAULT}>Account default</option>
                {options.owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
            {kind === 'add_tag' && (
              <>
                <Field label="Tag" value={value} onChange={(e) => setValue(e.target.value)} list={tagList} autoComplete="off" />
                <datalist id={tagList}>
                  {options.tags.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </>
            )}
            {skipsPending && pendingCount > 0 && (
              <p id={PENDING_ID} className="dialog__body">
                {`${pendingCount} pending transaction${pendingCount === 1 ? '' : 's'} will be skipped — they can't be edited until the bank posts them.`}
              </p>
            )}
            {stale && (
              <p id={STALE_ID} role="note" className="dialog__body">
                {STALE_NOTE}
              </p>
            )}
            <div className="dialog__actions">
              <Button type="button" size="page" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                size="page"
                variant="primary"
                aria-disabled={!chosen || targets === 0 || stale || undefined}
                aria-describedby={describedBy(stale, targets === 0 && skipsPending && pendingCount > 0)}
                onClick={() => {
                  if (chosen && targets > 0 && !stale) void go(rows, { updated: 0, skipped: 0 })
                }}
              >
                {`Apply to ${targets}`}
              </Button>
            </div>
          </>
        )}

        {phase.t === 'running' && (
          <>
            {/* Seen, not spoken: the live region above speaks at milestones. */}
            <p className="dialog__body" aria-hidden="true">
              {progress ? `Updating ${progress.done} of ${progress.total}…` : 'Updating…'}
            </p>
          </>
        )}

        {phase.t === 'result' && (
          <Result
            phase={phase}
            rows={rows}
            stale={stale}
            onRetry={() => {
              const ids = new Set(phase.failed.map((f) => f.id))
              void go(
                rows.filter((r) => ids.has(r.id)),
                { updated: phase.updated, skipped: phase.skipped },
              )
            }}
            onClose={onClose}
          />
        )}
      </div>
    </Overlay>
  )
}

function summary(phase: Extract<Phase, { t: 'result' }>): string {
  return phase.failed.length === 0 ? `${phase.updated} updated` : `${phase.updated} updated, ${phase.failed.length} failed`
}

function Result({
  phase,
  rows,
  stale,
  onRetry,
  onClose,
}: {
  phase: Extract<Phase, { t: 'result' }>
  rows: readonly Txn[]
  stale: boolean
  onRetry: () => void
  onClose: () => void
}) {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const failed = phase.failed
  return (
    <>
      <p className="dialog__body" aria-hidden="true">
        {summary(phase)}
      </p>
      {failed.length > 0 && (
        <ul className="txn-bulk-dialog__failures">
          {failed.map((f) => {
            const row = byId.get(f.id)
            return (
              <li key={f.id}>
                <strong>{row?.merchant ?? 'Transaction'}</strong>
                {row && ` · ${shortDate(row.date)}`}
                {` — ${f.reason}`}
              </li>
            )
          })}
        </ul>
      )}
      {phase.skipped > 0 && (
        <p className="dialog__body">
          {`${phase.skipped} pending transaction${phase.skipped === 1 ? ' was' : 's were'} skipped — they can't be edited until the bank posts them.`}
        </p>
      )}
      {stale && failed.length > 0 && (
        <p id={STALE_ID} role="note" className="dialog__body">
          {STALE_NOTE}
        </p>
      )}
      <div className="dialog__actions">
        {failed.length > 0 && (
          <Button
            type="button"
            size="page"
            variant="outline"
            aria-disabled={stale || undefined}
            aria-describedby={stale ? STALE_ID : undefined}
            onClick={() => {
              if (!stale) onRetry()
            }}
          >
            Retry failed
          </Button>
        )}
        <Button type="button" size="page" variant={failed.length > 0 ? 'chip' : 'primary'} onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  )
}
